/**
 * Form1008Content Component
 *
 * Displays Form 1008 (Uniform Underwriting and Transmittal Summary) content.
 * This is the content portion used within the tabbed LoanDetailsSheet.
 */

import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form1008Field,
  Form1008CheckField,
  Form1008OptionGroup
} from './Form1008Field';
import type { ProspectLoan } from './types';
import {
  Building2,
  FileText,
  Calculator,
  Briefcase
} from 'lucide-react';

interface Form1008ContentProps {
  loan: ProspectLoan;
}

/**
 * Section header component matching PDF section styling
 */
function SectionHeader({
  number,
  title,
  icon: Icon
}: {
  number: string;
  title: string;
  icon: React.ElementType;
}) {
  return (
    <div className="flex items-center gap-3 bg-muted/50 px-4 py-3 rounded-lg border">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="font-semibold text-lg">
        {number}. {title}
      </span>
    </div>
  );
}

/**
 * Loan Type options matching Form 1008
 */
const LOAN_TYPE_OPTIONS = [
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'VA', label: 'VA' },
  { value: 'USDA', label: 'USDA' },
  { value: 'Other', label: 'Other' }
];

/**
 * Amortization Type options
 */
const AMORTIZATION_OPTIONS = [
  { value: 'Fixed', label: 'Fixed Rate' },
  { value: 'ARM', label: 'ARM' },
  { value: 'GPM', label: 'GPM' },
  { value: 'Balloon', label: 'Balloon' },
  { value: 'Other', label: 'Other' }
];

/**
 * Property Type options
 */
const PROPERTY_TYPE_OPTIONS = [
  { value: 'Detached', label: 'Detached' },
  { value: 'Attached', label: 'Attached' },
  { value: 'Condo', label: 'Condo' },
  { value: 'PUD', label: 'PUD' },
  { value: 'Cooperative', label: 'Co-op' },
  { value: 'ManufacturedHousing', label: 'MH' }
];

/**
 * Occupancy options
 */
const OCCUPANCY_OPTIONS = [
  { value: 'PrimaryResidence', label: 'Primary Residence' },
  { value: 'SecondHome', label: 'Second Home' },
  { value: 'Investor', label: 'Investment' }
];

export function Form1008Content({ loan }: Form1008ContentProps) {
  // Format full borrower name
  const borrowerName = [loan.borrower_first_name, loan.borrower_last_name]
    .filter(Boolean)
    .join(' ') || 'Unknown Borrower';

  // Format full property address
  const fullAddress = [
    loan.property_address,
    loan.subject_property_city,
    loan.subject_property_state,
    loan.property_zip
  ]
    .filter(Boolean)
    .join(', ');

  // Format broker address
  const brokerAddress = [
    loan.broker_address,
    loan.broker_city,
    loan.broker_state,
    loan.broker_zip
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-8">
      {/* Form Title */}
      <div className="text-center border-b pb-4">
        <h2 className="text-xl font-semibold">Uniform Underwriting and Transmittal Summary</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Fannie Mae Form 1008 / Freddie Mac Form 1077
        </p>
      </div>

      {/* Section I: Borrower and Property Information */}
      <section className="space-y-4">
        <SectionHeader
          number="I"
          title="Borrower and Property Information"
          icon={Building2}
        />

        <Card>
          <CardContent className="pt-6">
            {/* Borrower Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Form1008Field
                label="Borrower Name"
                value={borrowerName}
              />
              <Form1008Field
                label="Property Address"
                value={fullAddress}
              />
            </div>

            <Separator className="my-4" />

            {/* Property Details */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Form1008Field
                label="Sales Price"
                value={loan.trans_details_purchase_price}
                format="currency"
              />
              <Form1008Field
                label="Appraised Value"
                value={loan.property_value}
                format="currency"
              />
              <Form1008Field
                label="No. of Units"
                value={loan.property_units}
                format="number"
              />
            </div>

            {/* Property Type */}
            <div className="grid grid-cols-2 gap-6">
              <Form1008OptionGroup
                label="Property Type"
                options={PROPERTY_TYPE_OPTIONS}
                selectedValue={loan.property_type}
              />
              <Form1008OptionGroup
                label="Occupancy Status"
                options={OCCUPANCY_OPTIONS}
                selectedValue={loan.occupancy_status}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Section II: Mortgage Information */}
      <section className="space-y-4">
        <SectionHeader
          number="II"
          title="Mortgage Information"
          icon={FileText}
        />

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Note Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <Form1008Field
                label="Loan Amount"
                value={loan.trans_details_loan_amt}
                format="currency"
              />
              <Form1008Field
                label="Note Rate"
                value={loan.trans_details_interest_rate}
                format="percentage"
              />
              <Form1008Field
                label="Loan Term"
                value={loan.loan_term_months}
                format="months"
              />
              <Form1008Field
                label="Lien Position"
                value={loan.lien_position}
              />
            </div>

            {/* Loan Type */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <Form1008OptionGroup
                label="Loan Type"
                options={LOAN_TYPE_OPTIONS}
                selectedValue={loan.trans_details_loan_type}
              />
              <Form1008OptionGroup
                label="Amortization Type"
                options={AMORTIZATION_OPTIONS}
                selectedValue={loan.amortization_type}
              />
            </div>

            {/* Ratios */}
            <div className="grid grid-cols-3 gap-4">
              <Form1008Field
                label="LTV"
                value={loan.freddie_mac_ltv_ratio_pct}
                format="percentage"
              />
              <Form1008Field
                label="CLTV/TLTV"
                value={loan.trans_details_cltv_ratio_pct}
                format="percentage"
              />
              <Form1008Field
                label="HCLTV/HTLTV"
                value={loan.hcltv_ratio}
                format="percentage"
              />
            </div>
          </CardContent>
        </Card>

        {/* Originator Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mortgage Originator</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Form1008Field
                label="Broker/Lender Name"
                value={loan.broker_name}
              />
              <Form1008Field
                label="Broker/Lender Address"
                value={brokerAddress}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Section III: Underwriting Information */}
      <section className="space-y-4">
        <SectionHeader
          number="III"
          title="Underwriting Information"
          icon={Calculator}
        />

        {/* Contacts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Form1008Field
                label="Underwriter Name"
                value={loan.underwriter_name}
              />
              <div className="flex items-center gap-4">
                <Form1008CheckField
                  label="Self-Employed"
                  checked={loan.self_employed_indicator}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Income */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Stable Monthly Income</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Form1008Field
                label="Total Monthly Income"
                value={loan.total_monthly_income}
                format="currency"
              />
              <Form1008Field
                label="Credit Score"
                value={loan.representative_credit_score}
                format="number"
              />
            </div>
          </CardContent>
        </Card>

        {/* Ratios */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Qualifying Ratios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <Form1008Field
                label="Housing Expense Ratio"
                value={loan.housing_expense_ratio}
                format="percentage"
              />
              <Form1008Field
                label="Total Debt Ratio (DTI)"
                value={loan.dti_ratio}
                format="percentage"
              />
              <Form1008Field
                label="Qualifying Rate"
                value={loan.trans_details_qual_rate}
                format="percentage"
              />
            </div>
          </CardContent>
        </Card>

        {/* Proposed Monthly Payment */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Proposed Monthly Payment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <Form1008Field
                label="First Mortgage P&I"
                value={loan.proposed_pi_payment}
                format="currency"
              />
              <Form1008Field
                label="Hazard Insurance"
                value={loan.proposed_hazard_insurance}
                format="currency"
              />
              <Form1008Field
                label="Property Taxes"
                value={loan.proposed_property_taxes}
                format="currency"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Form1008Field
                label="MI Payment"
                value={loan.proposed_mi_payment}
                format="currency"
              />
              <div /> {/* Spacer */}
              <div className="border-t pt-2">
                <Form1008Field
                  label="Total Monthly Payment"
                  value={loan.proposed_total_payment}
                  format="currency"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Funds to Close */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Funds to Close</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <Form1008Field
                label="Required Funds to Close"
                value={loan.required_funds_to_close}
                format="currency"
              />
              <Form1008Field
                label="Verified Assets"
                value={loan.verified_assets}
                format="currency"
              />
              <Form1008Field
                label="Months Reserves"
                value={loan.months_reserves}
                format="number"
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Section IV: Seller/Contact Information */}
      <section className="space-y-4">
        <SectionHeader
          number="IV"
          title="Seller and Contact Information"
          icon={Briefcase}
        />

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Form1008Field
                label="Seller Name"
                value={loan.broker_name}
              />
              <Form1008Field
                label="Seller Loan Number"
                value={loan.trans_details_loan_number}
              />
            </div>

            <Separator className="my-4" />

            <div className="grid grid-cols-2 gap-4">
              <Form1008Field
                label="Loan Officer"
                value={
                  loan.loan_officer_first_name && loan.loan_officer_last_name
                    ? `${loan.loan_officer_first_name} ${loan.loan_officer_last_name}`
                    : null
                }
              />
              <Form1008Field
                label="Loan Status"
                value={loan.trans_details_loan_status || loan.milestone}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
        <p>Data synced from Encompass LOS</p>
        <p className="mt-1">
          Last updated: {loan.updated_at ? new Date(loan.updated_at).toLocaleString() : 'Unknown'}
        </p>
      </div>
    </div>
  );
}
